import { toast } from 'sonner';

import { useUpdateAsset } from '@/hooks/useAssets';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { parseKeys } from '@/sections/SearchByType';
import { AssetStatus } from '@/types';

export interface UseAssetStatusHandlersParams {
  /** Current bulk selection state — used to decide per-item vs bulk path */
  selectionState?: BulkSelectionState;
  /**
   * Called with the requested status when shouldUseBulk(selectionState) is true.
   * The caller (index.tsx) shows a BulkConfirmationDialog and submits the bulk job.
   */
  onBulkRequested?: (status: AssetStatus) => void;
}

/**
 * Custom hook for asset status update handlers
 * Handles freeze, unfreeze, and delete operations
 *
 * When selectionState indicates a bulk operation (mode='all' or manual > BULK_THRESHOLD),
 * the handler delegates to onBulkRequested instead of per-item mutations.
 *
 * @returns Object containing handleUpdateStatus function
 */
export const useAssetStatusHandlers = ({
  selectionState,
  onBulkRequested,
}: UseAssetStatusHandlersParams = {}) => {
  const { mutateAsync: updateAsset } = useUpdateAsset();

  /**
   * Updates the status of multiple assets
   * Executes updates in parallel with graceful error handling
   *
   * Uses Promise.allSettled to handle partial failures and provide
   * accurate success/failure feedback to users.
   *
   * @param assets - Assets to update
   * @param status - New status to apply
   */
  const handleUpdateStatus = async (
    assets: { key: string }[],
    status: AssetStatus
  ) => {
    // Delegate to bulk endpoint when selection warrants it (BULK_THRESHOLD=2,
    // so any multi-row selection goes through the bulk path).
    if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
      onBulkRequested(status);
      return;
    }

    if (assets.length === 0) return;

    const isSingle = assets.length === 1;

    const results = await Promise.allSettled(
      assets.map(asset =>
        updateAsset({
          key: asset.key,
          name: parseKeys.assetKey(asset.key).identifier,
          status,
          showSnackbar: isSingle,
        })
      )
    );

    const failures = results.filter(r => r.status === 'rejected');
    const succeeded = results.length - failures.length;

    if (isSingle) {
      if (failures.length > 0) {
        toast.error(`Failed to update asset`);
        return;
      }
    } else {
      // Multi-asset: show one aggregated summary
      if (failures.length > 0) {
        toast.error(
          `Updated ${succeeded} of ${results.length} assets (${failures.length} failed).`
        );
      } else {
        toast.success(`Updated ${succeeded} assets`);
      }
    }

    queryClient.invalidateQueries({
      queryKey: getQueryKey.getMy('asset'),
    });
  };

  return { handleUpdateStatus };
};
